/**
 * The unified permission catalogue for the whole platform.
 *
 * One model replaces the three that came before it:
 *
 *   - Ledgerly used `module.submodule` x action, stored in `role_permissions`.
 *     It was the richest of the three, so it is the shape we keep.
 *   - The ERP (Java) used Permission + PermissionAction, the same idea with
 *     different names.
 *   - The HRMS used a coarse `Role` enum with a hardcoded role -> permission
 *     map and `resource:action` strings. Those strings are preserved in
 *     LEGACY_HR_PERMISSIONS below so the HR module's existing guards keep
 *     working while they migrate onto module keys.
 *
 * A permission is the pair (module, action). Nothing in the platform may be
 * guarded by anything else — no ad-hoc role checks scattered through modules.
 */
export const ACTIONS = [
    'view',
    'create',
    'edit',
    'delete',
    'approve',
    'cancel',
    'reverse',
    'print',
    'export',
    'import',
    'configure',
    'freeze',
    'unfreeze',
];
const CRUD = ['view', 'create', 'edit', 'delete'];
const CRUDA = [...CRUD, 'approve'];
const VIEW = ['view'];
const REPORT = ['view', 'print', 'export'];
const TXN = ['view', 'create', 'edit', 'delete', 'approve', 'cancel', 'reverse', 'print', 'export'];
export const MODULE_GROUPS = [
    {
        key: 'overview',
        label: 'Overview',
        modules: [{ key: 'dashboard', label: 'Dashboard', actions: VIEW }],
    },
    {
        key: 'masters',
        label: 'Master data',
        modules: [
            { key: 'master.group', label: 'Account groups', actions: CRUD },
            { key: 'master.account', label: 'Accounts', actions: CRUD },
            { key: 'master.party', label: 'Parties (customers & vendors)', actions: [...CRUD, 'import', 'export'] },
            { key: 'master.product', label: 'Products', actions: [...CRUD, 'import', 'export'] },
            { key: 'master.unit', label: 'Units & conversions', actions: CRUD },
            { key: 'master.pricelist', label: 'Price lists', actions: CRUD },
            { key: 'master.gst', label: 'GST slabs & commodities', actions: CRUD },
            { key: 'master.hsn', label: 'HSN codes', actions: CRUD },
            { key: 'master.warehouse', label: 'Warehouses & locations', actions: CRUD },
            { key: 'master.batch', label: 'Batches', actions: CRUD },
            { key: 'master.process', label: 'Processes', actions: CRUD },
            { key: 'master.bom', label: 'Bills of material', actions: CRUD },
            { key: 'master.costcentre', label: 'Cost centres', actions: CRUD },
            { key: 'master.asset', label: 'Fixed assets', actions: CRUD },
            { key: 'master.employee', label: 'Employee master', actions: CRUD },
            { key: 'master.territory', label: 'Territories', actions: CRUD },
            { key: 'master.outlet', label: 'Outlets', actions: CRUD },
            { key: 'master.distributor', label: 'Distributors', actions: CRUD },
            { key: 'master.vendor', label: 'Vendors', actions: CRUD },
        ],
    },
    {
        key: 'accounting',
        label: 'Accounting',
        modules: [
            { key: 'txn.sales', label: 'Sales entries', actions: TXN },
            { key: 'txn.purchase', label: 'Purchase entries', actions: TXN },
            { key: 'txn.receipt', label: 'Receipts', actions: TXN },
            { key: 'txn.payment', label: 'Payments', actions: TXN },
            { key: 'txn.contra', label: 'Contra', actions: TXN },
            { key: 'txn.journal', label: 'Journal', actions: TXN },
            { key: 'txn.creditnote', label: 'Credit notes', actions: TXN },
            { key: 'txn.debitnote', label: 'Debit notes', actions: TXN },
            { key: 'txn.stock', label: 'Stock vouchers', actions: TXN },
            { key: 'txn.production', label: 'Production vouchers', actions: TXN },
            { key: 'txn.order', label: 'Orders', actions: TXN },
            { key: 'txn.quotation', label: 'Quotations', actions: TXN },
            { key: 'banking.reconcile', label: 'Bank reconciliation', actions: [...CRUD, 'export', 'print'] },
            { key: 'tds.master', label: 'TDS sections', actions: CRUD },
            { key: 'tds.entry', label: 'TDS entries & challans', actions: [...CRUD, 'export', 'print'] },
            { key: 'tds.report', label: 'TDS reports', actions: REPORT },
        ],
    },
    {
        key: 'gst',
        label: 'GST & compliance',
        modules: [
            { key: 'gst.register', label: 'GST registers', actions: REPORT },
            { key: 'gst.return', label: 'GST returns', actions: [...REPORT, 'create', 'edit'] },
            { key: 'gst.recon', label: 'GSTR-2B reconciliation', actions: [...REPORT, 'create'] },
            { key: 'gst.integrity', label: 'GST integrity checks', actions: REPORT },
            { key: 'gst.audit', label: 'GST audit', actions: REPORT },
            { key: 'gst.einvoice', label: 'e-Invoice', actions: [...REPORT, 'create', 'cancel'] },
            { key: 'gst.ewaybill', label: 'e-Way bill', actions: [...REPORT, 'create', 'cancel'] },
        ],
    },
    {
        key: 'hr',
        label: 'HR & People',
        modules: [
            { key: 'hr.employee', label: 'Employees', actions: [...CRUD, 'export'] },
            { key: 'hr.document', label: 'Employee documents', actions: CRUD },
            { key: 'hr.orgchart', label: 'Org structure', actions: CRUD },
            { key: 'hr.attendance', label: 'Attendance', actions: [...CRUDA, 'export'] },
            { key: 'hr.attendance.rule', label: 'Attendance rules', actions: [...CRUD, 'configure'] },
            { key: 'hr.attendance.device', label: 'Biometric devices', actions: [...CRUD, 'configure'] },
            { key: 'hr.regularisation', label: 'Regularisations', actions: CRUDA },
            { key: 'hr.leave', label: 'Leave', actions: [...CRUDA, 'export'] },
            { key: 'hr.payroll', label: 'Payroll runs', actions: [...CRUDA, 'export', 'freeze'] },
            { key: 'hr.payslip', label: 'Payslips', actions: [...REPORT, 'create'] },
            { key: 'hr.expense', label: 'Employee expenses', actions: CRUDA },
            { key: 'hr.advance', label: 'Salary advances', actions: CRUDA },
            { key: 'hr.loan', label: 'Loans', actions: CRUDA },
            { key: 'hr.recruitment', label: 'Recruitment', actions: CRUDA },
            { key: 'hr.letter', label: 'Letters', actions: [...CRUD, 'print'] },
            { key: 'hr.training', label: 'Training', actions: CRUD },
            { key: 'hr.performance', label: 'Performance', actions: CRUDA },
            { key: 'hr.exit', label: 'Exit & separation', actions: CRUDA },
            { key: 'hr.holiday', label: 'Holiday calendar', actions: CRUD },
            { key: 'hr.shift', label: 'Shifts', actions: CRUD },
            { key: 'hr.announcement', label: 'Announcements', actions: CRUD },
            { key: 'hr.report', label: 'HR reports', actions: REPORT },
            { key: 'hr.selfservice', label: 'My space (own records)', actions: ['view', 'create', 'edit'] },
        ],
    },
    {
        key: 'erp',
        label: 'Operations',
        modules: [
            { key: 'erp.procurement.requisition', label: 'Purchase requisitions', actions: CRUDA },
            { key: 'erp.procurement.po', label: 'Purchase orders', actions: [...CRUDA, 'print', 'cancel'] },
            { key: 'erp.procurement.grn', label: 'Goods receipt notes', actions: [...CRUDA, 'print'] },
            { key: 'erp.procurement.vendor', label: 'Vendor management', actions: CRUD },
            { key: 'erp.procurement.return', label: 'Purchase returns', actions: CRUDA },
            { key: 'erp.production.bom', label: 'Production BOM', actions: CRUD },
            { key: 'erp.production.order', label: 'Production orders', actions: [...CRUDA, 'cancel'] },
            { key: 'erp.production.batch', label: 'Production batches', actions: CRUDA },
            { key: 'erp.production.cost', label: 'Production costing', actions: REPORT },
            { key: 'erp.packaging.material', label: 'Packaging materials', actions: CRUD },
            { key: 'erp.packaging.order', label: 'Packaging orders', actions: CRUDA },
            { key: 'erp.packaging.batch', label: 'Packaging batches', actions: CRUDA },
            { key: 'erp.warehouse.stock', label: 'Stock balances', actions: REPORT },
            { key: 'erp.warehouse.transfer', label: 'Stock transfers', actions: CRUDA },
            { key: 'erp.warehouse.adjustment', label: 'Stock adjustments', actions: CRUDA },
            { key: 'erp.warehouse.verification', label: 'Physical verification', actions: CRUDA },
            { key: 'erp.quality.check', label: 'Quality checks', actions: CRUDA },
            { key: 'erp.quality.format', label: 'QC formats', actions: [...CRUD, 'configure'] },
            { key: 'erp.dispatch', label: 'Dispatch', actions: [...CRUDA, 'print', 'cancel'] },
            { key: 'erp.sales', label: 'Sales operations', actions: CRUDA },
            { key: 'erp.gtm.target', label: 'GTM targets', actions: [...CRUD, 'import', 'export'] },
            { key: 'erp.scheme', label: 'Schemes', actions: [...CRUDA, 'import', 'export'] },
            { key: 'erp.pricing', label: 'Pricing & price changes', actions: CRUDA },
            { key: 'erp.campaign', label: 'Campaigns', actions: CRUD },
            { key: 'erp.asset', label: 'Assets', actions: [...CRUD, 'import', 'export'] },
            { key: 'erp.task', label: 'Tasks', actions: [...CRUD, 'approve'] },
            { key: 'erp.chat', label: 'Chat', actions: ['view', 'create', 'delete'] },
            { key: 'erp.request', label: 'Requests', actions: CRUDA },
        ],
    },
    {
        key: 'field',
        label: 'Field sales',
        modules: [
            // Day-to-day field work
            { key: 'field.dashboard', label: 'Field dashboard', actions: VIEW },
            { key: 'field.discipline', label: 'Discipline (live map, daily summary)', actions: ['view', 'export'] },
            { key: 'field.tracking', label: 'Route playback', actions: VIEW },
            { key: 'field.visit', label: 'Visits & attendance', actions: [...CRUD, 'export'] },
            { key: 'field.order', label: 'Field orders', actions: [...CRUDA, 'print', 'export'] },
            { key: 'field.collection', label: 'Field collections', actions: ['view', 'create', 'edit', 'approve', 'export'] },
            { key: 'field.sales_report', label: 'User wise sales', actions: REPORT },
            { key: 'field.journey', label: 'Journey plans', actions: [...CRUDA, 'import', 'export', 'configure'] },
            { key: 'field.beat', label: 'Beats', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.survey', label: 'Surveys', actions: [...CRUD, 'export'] },
            { key: 'field.request', label: 'General trade requests', actions: [...CRUDA, 'export'] },
            { key: 'field.reward', label: 'Reward requests', actions: ['view', 'approve', 'configure', 'export'] },
            // GTM strategy
            { key: 'field.cohort', label: 'Cohorts', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.must_sell', label: 'Must sell rules', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.focus', label: 'Focus products', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.flex_target', label: 'Flexible targets', actions: [...CRUD, 'import', 'export', 'configure'] },
            { key: 'field.segmentation', label: 'Outlet segmentation', actions: ['view', 'configure'] },
            { key: 'field.scheme_basket', label: 'Scheme baskets', actions: CRUD },
            { key: 'field.order_block', label: 'Order blocks', actions: CRUD },
            // Expenses
            { key: 'field.expense', label: 'Expense claims & approvals', actions: [...CRUDA, 'export'] },
            { key: 'field.expense_policy', label: 'TA-DA policy', actions: ['view', 'configure'] },
            { key: 'field.expense_settlement', label: 'Expense settlement', actions: ['view', 'approve', 'export'] },
            // FA One learning
            { key: 'field.faone.content', label: 'FA One content', actions: CRUD },
            { key: 'field.faone.course', label: 'FA One courses', actions: CRUDA },
            { key: 'field.faone.audit', label: 'FA One audit surveys', actions: [...CRUDA, 'export'] },
            { key: 'field.faone.learn', label: 'FA One learning (learner)', actions: ['view', 'create'] },
            // Analytics
            { key: 'field.analytics.report', label: 'Field reports', actions: REPORT },
            { key: 'field.analytics.flexible', label: 'Flexible reports', actions: [...CRUD, 'export'] },
            { key: 'field.analytics.quickviz', label: 'Quick viz', actions: VIEW },
            // Masters and configuration
            { key: 'field.master.position', label: 'Positions', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.master.user', label: 'Field users', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.master.geography', label: 'Geography', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.outlet', label: 'Outlets', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.distributor', label: 'Distributors', actions: [...CRUD, 'import', 'export'] },
            { key: 'field.config', label: 'Field configurations', actions: ['view', 'configure'] },
            { key: 'field.api_config', label: 'API credentials', actions: ['view', 'configure'] },
            // Miscellaneous
            { key: 'field.kra', label: 'KRA targets', actions: CRUD },
            { key: 'field.external_asset', label: 'External assets', actions: CRUD },
            { key: 'field.api_usage', label: 'External API summary', actions: VIEW },
            { key: 'field.issue', label: 'Issues & service desk', actions: [...CRUDA, 'export'] },
        ],
    },
    {
        key: 'insights',
        label: 'Insights',
        modules: [
            { key: 'report.books', label: 'Account books', actions: REPORT },
            { key: 'report.outstanding', label: 'Outstanding', actions: REPORT },
            { key: 'report.register', label: 'Registers', actions: REPORT },
            { key: 'report.financial', label: 'Financial statements', actions: REPORT },
            { key: 'report.analysis', label: 'Analysis', actions: REPORT },
            { key: 'report.stock', label: 'Stock reports', actions: REPORT },
            { key: 'report.order', label: 'Order reports', actions: REPORT },
            { key: 'report.production', label: 'Production reports', actions: REPORT },
            { key: 'report.other', label: 'Other reports', actions: REPORT },
            { key: 'analytics', label: 'Analytics', actions: REPORT },
        ],
    },
    {
        key: 'utilities',
        label: 'Utilities',
        modules: [
            { key: 'utility.backup', label: 'Backup & restore', actions: ['view', 'create'] },
            { key: 'utility.freeze', label: 'Data freeze', actions: ['view', 'freeze', 'unfreeze'] },
            { key: 'utility.advanced', label: 'Advanced utilities', actions: ['view', 'edit'] },
            { key: 'utility.import', label: 'Data import', actions: ['view', 'create'] },
            { key: 'utility.yearend', label: 'Year end', actions: ['view', 'create'] },
            { key: 'utility.diary', label: 'Diary', actions: CRUD },
            { key: 'utility.repair', label: 'Data health', actions: ['view', 'edit'] },
        ],
    },
    {
        key: 'setup',
        label: 'Setup',
        modules: [
            { key: 'setup.company', label: 'Company setup', actions: ['view', 'edit', 'configure'] },
            { key: 'setup.fy', label: 'Financial years', actions: [...CRUD, 'freeze'] },
            { key: 'setup.voucher', label: 'Voucher types', actions: [...CRUD, 'configure'] },
            { key: 'setup.numbering', label: 'Voucher numbering', actions: ['view', 'configure'] },
            { key: 'setup.sales', label: 'Sales setup', actions: ['view', 'configure'] },
            { key: 'setup.purchase', label: 'Purchase setup', actions: ['view', 'configure'] },
            { key: 'setup.security', label: 'Security policy', actions: ['view', 'configure'] },
            { key: 'setup.software', label: 'Software settings', actions: ['view', 'configure'] },
            { key: 'setup.format', label: 'Print formats', actions: [...CRUD, 'configure'] },
            { key: 'setup.approval', label: 'Approval rules', actions: [...CRUD, 'configure'] },
        ],
    },
    {
        key: 'admin',
        label: 'Administration',
        modules: [
            { key: 'admin.user', label: 'Users', actions: CRUD },
            { key: 'admin.role', label: 'Roles & permissions', actions: [...CRUD, 'configure'] },
            { key: 'admin.audit', label: 'Audit log', actions: REPORT },
            { key: 'admin.company', label: 'Companies', actions: CRUD },
            { key: 'admin.branch', label: 'Branches', actions: CRUD },
            { key: 'admin.department', label: 'Departments', actions: CRUD },
            { key: 'admin.designation', label: 'Designations', actions: CRUD },
            { key: 'admin.settings', label: 'Platform settings', actions: ['view', 'edit', 'configure'] },
            { key: 'admin.notification', label: 'Notification settings', actions: ['view', 'configure'] },
            { key: 'admin.integration', label: 'Integrations', actions: ['view', 'configure'] },
        ],
    },
];
/** Flat list of every module definition. */
export const MODULES = MODULE_GROUPS.flatMap((g) => g.modules);
/** Flat list of every module key. Stored in `role_permissions.module`. */
export const MODULE_KEYS = MODULES.map((m) => m.key);
const MODULE_BY_KEY = new Map(MODULES.map((m) => [m.key, m]));
export function moduleDef(key) {
    return MODULE_BY_KEY.get(key);
}
export function isKnownModule(key) {
    return MODULE_BY_KEY.has(key);
}
// ---------------------------------------------------------------------------
// Compatibility: the HRMS `resource:action` strings
// ---------------------------------------------------------------------------
/**
 * The HR module's existing code (and its React pages) guard on strings like
 * `employee:manage`. Rather than rewrite ~180 call sites in working code, we
 * translate: each legacy string maps to the (module, action) pairs that grant
 * it. `hasLegacyHrPermission` below is what the HR guards call.
 *
 * `manage` in the old model meant create + edit + delete, so it is satisfied
 * by holding any of them — the finer-grained check still happens at the
 * module level for anything newly written.
 */
export const LEGACY_HR_PERMISSIONS = {
    'user:manage': [['admin.user', 'edit']],
    'role:manage': [['admin.role', 'edit']],
    'settings:manage': [['admin.settings', 'edit']],
    'audit:read': [['admin.audit', 'view']],
    'master:read': [['master.employee', 'view']],
    'master:manage': [['master.employee', 'edit']],
    'employee:read': [['hr.employee', 'view']],
    'employee:manage': [['hr.employee', 'edit']],
    'recruitment:read': [['hr.recruitment', 'view']],
    'recruitment:manage': [['hr.recruitment', 'edit']],
    'letter:read': [['hr.letter', 'view']],
    'letter:manage': [['hr.letter', 'edit']],
    'attendance:read': [['hr.attendance', 'view']],
    'attendance:manage': [['hr.attendance', 'edit']],
    'attendance:approve': [['hr.attendance', 'approve']],
    'leave:read': [['hr.leave', 'view']],
    'leave:manage': [['hr.leave', 'edit']],
    'leave:approve': [['hr.leave', 'approve']],
    'payroll:read': [['hr.payroll', 'view']],
    'payroll:manage': [['hr.payroll', 'edit']],
    'finance:read': [['hr.expense', 'view'], ['hr.advance', 'view'], ['hr.loan', 'view']],
    'finance:manage': [['hr.expense', 'edit'], ['hr.advance', 'edit'], ['hr.loan', 'edit']],
    'finance:approve': [['hr.expense', 'approve'], ['hr.advance', 'approve'], ['hr.loan', 'approve']],
    'performance:read': [['hr.performance', 'view']],
    'performance:manage': [['hr.performance', 'edit']],
    'training:read': [['hr.training', 'view']],
    'training:manage': [['hr.training', 'edit']],
    'exit:read': [['hr.exit', 'view']],
    'exit:manage': [['hr.exit', 'edit']],
    'exit:approve': [['hr.exit', 'approve']],
    'report:read': [['hr.report', 'view']],
    'self:service': [['hr.selfservice', 'view']],
};
function grants(granted, action) {
    if (!granted)
        return false;
    return granted instanceof Set ? granted.has(action) : granted.includes(action);
}
function grantsAny(granted) {
    if (!granted)
        return false;
    return granted instanceof Set ? granted.size > 0 : granted.length > 0;
}
export const EMPTY_PERMISSIONS = { superadmin: false, map: {} };
export function can(perms, moduleKey, action) {
    if (!perms)
        return false;
    if (perms.superadmin)
        return true;
    return grants(perms.map?.[moduleKey], action);
}
/** True if the user holds any action at all on the module — used for menu visibility. */
export function canAccess(perms, moduleKey) {
    if (!perms)
        return false;
    if (perms.superadmin)
        return true;
    return grantsAny(perms.map?.[moduleKey]);
}
/** Resolve one of the HR module's legacy `resource:action` strings. */
export function hasLegacyHrPermission(perms, legacy) {
    if (!perms)
        return false;
    if (perms.superadmin)
        return true;
    const pairs = LEGACY_HR_PERMISSIONS[legacy];
    if (!pairs)
        return false;
    return pairs.some(([moduleKey, action]) => can(perms, moduleKey, action));
}
//# sourceMappingURL=permissions.js.map